// ── 홍보물 검색 자동완성 인풋 ──────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MOBILE_BREAKPOINT } from './utils';
import { inputStyle } from './styles';

export function ItemSearchInput({ value, unitPrice, promotionalItemsList, onSelect, autoFocus = false, onAutoFocused }) {
  const [query, setQuery]     = useState('');
  const [open, setOpen]       = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  // autoFocus prop이 true가 되면 자동 포커스 + 모바일 상단 스크롤
  // (+홍보물 추가 버튼 클릭 시에만 트리거 — 수동 탭은 해당 없음)
  useEffect(() => {
    if (!autoFocus) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus(); // 브라우저 자동 스크롤 허용 (preventScroll은 오히려 방해)
    onAutoFocused?.();

    if (window.innerWidth > MOBILE_BREAKPOINT) return;

    // 키보드 완전히 열린 후 검색창을 모달 상단 근처로 즉각 스크롤
    const scrollToSafe = () => {
      const el2 = inputRef.current;
      if (!el2) return;

      // 가장 가까운 overflowY:auto/scroll 조상(모달 바디) 탐색
      // scrollHeight > clientHeight 조건 제거 — 나중에 padding으로 확보할 것
      let container = el2.parentElement;
      while (container && container !== document.documentElement) {
        const s = window.getComputedStyle(container);
        if (/(auto|scroll)/.test(s.overflow + s.overflowY)) break;
        container = container.parentElement;
      }
      if (!container || container === document.documentElement) return;

      const containerTop = container.getBoundingClientRect().top;
      const elTop        = el2.getBoundingClientRect().top;
      const needed       = elTop - containerTop - 20; // 컨테이너 상단 20px 아래에 놓으려면 필요한 scrollBy 양

      if (needed <= 0) return; // 이미 위에 있음

      // 새 아이템은 목록 맨 마지막 → 아래 스크롤 가능 범위가 부족할 수 있음
      // paddingBottom으로 임시 공간을 확보해 scrollBy가 충분히 작동하게 함
      const available = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (available < needed) {
        container.style.paddingBottom = (needed - available + 40) + 'px';
      }

      // paddingBottom 추가로 elTop이 바뀌었으므로 다시 계산
      const elTop2 = el2.getBoundingClientRect().top;
      container.scrollBy({ top: elTop2 - container.getBoundingClientRect().top - 20, behavior: 'instant' });
    };

    if (window.visualViewport) {
      // visualViewport.resize = 키보드가 완전히 열린 시점
      const onVVResize = () => {
        window.visualViewport.removeEventListener('resize', onVVResize);
        clearTimeout(fallbackTimer);
        scrollToSafe();
      };
      // 키보드가 이미 열려있거나 resize 미발생 시 600ms 후 fallback
      const fallbackTimer = setTimeout(() => {
        window.visualViewport.removeEventListener('resize', onVVResize);
        scrollToSafe();
      }, 600);
      window.visualViewport.addEventListener('resize', onVVResize);
    } else {
      // visualViewport 미지원 환경: 500ms 고정 대기
      setTimeout(scrollToSafe, 500);
    }
  }, [autoFocus, onAutoFocused]);

  // 선택된 항목명을 표시하기 위한 display 값
  const displayName = value || '';

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // 검색어 필터링 후 발주량 내림차순 정렬 (최대 12개 표시)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? (promotionalItemsList || []).filter((p) => p.name.toLowerCase().includes(q))
      : (promotionalItemsList || []);
    return [...list]
      .sort((a, b) => (b.total_order_qty || 0) - (a.total_order_qty || 0))
      .slice(0, 12);
  }, [query, promotionalItemsList]);

  function handleFocus() {
    setFocused(true);
    setQuery('');
    setOpen(true);
  }

  function handleChange(e) {
    setQuery(e.target.value);
    setOpen(true);
  }

  function handleSelect(item) {
    onSelect(item);
    setOpen(false);
    setFocused(false);
    setQuery('');
  }

  function handleClear(e) {
    e.stopPropagation();
    onSelect(null);
    setQuery('');
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: '1 1 160px', minWidth: 0 }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          style={{ ...inputStyle, paddingRight: displayName ? 24 : 10, fontSize: 12 }}
          placeholder="홍보물 검색..."
          value={focused ? query : displayName}
          onFocus={handleFocus}
          onChange={handleChange}
        />
        {/* 단가 표시 (선택된 경우) */}
        {unitPrice > 0 && !focused && (
          <span style={{ position: 'absolute', right: 26, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'var(--text-muted)', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
            {Number(unitPrice).toLocaleString()}원
          </span>
        )}
        {/* 초기화 버튼 */}
        {displayName && (
          <button
            type="button"
            onMouseDown={handleClear}
            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: '0 2px', lineHeight: 1 }}
          >✕</button>
        )}
      </div>
      {/* 드롭다운 목록 */}
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2,
        }}>
          {filtered.map((p) => (
            <div
              key={p.id}
              onMouseDown={() => handleSelect(p)}
              style={{
                padding: '7px 10px', fontSize: 12, cursor: 'pointer', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center',
                borderBottom: '1px solid var(--bg-subtle)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-card)'; }}
            >
              <span style={{ color: 'var(--text-sub)' }}>{p.name}</span>
              <span style={{ color: '#7c3aed', fontWeight: 700, whiteSpace: 'nowrap', marginLeft: 8 }}>{Number(p.price).toLocaleString()}원</span>
            </div>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && query.trim() && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '10px', fontSize: 12, color: 'var(--text-muted)', marginTop: 2,
        }}>검색 결과 없음</div>
      )}
    </div>
  );
}
